import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { QUOTE_CONTEXTS, VERDICTS, type Verdict } from "@/data/quote-contexts";

const SITE = "https://mrbquotes.com";

export const Route = createFileRoute("/quotes/")({
  head: () => ({
    meta: [
      { title: "Famous Quotes Explained — Origins & Real Meanings" },
      {
        name: "description",
        content:
          "Where famous quotes really come from, what they originally meant, and how they get misused. Plain-English explainers with sources and honest uncertainty labels.",
      },
      { property: "og:title", content: "Famous Quotes Explained — Mr. B Quotes" },
      {
        property: "og:description",
        content: `${QUOTE_CONTEXTS.length} famous lines, traced to their real source and explained in plain English.`,
      },
      { property: "og:url", content: `${SITE}/quotes` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}/quotes` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Famous Quotes Explained",
          url: `${SITE}/quotes`,
          description:
            "Explainers on where famous quotations come from, what they meant, and how they are misused.",
          mainEntity: {
            "@type": "ItemList",
            itemListElement: QUOTE_CONTEXTS.map((q, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: q.quote,
              url: `${SITE}/quotes/${q.slug}`,
            })),
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            { "@type": "ListItem", position: 2, name: "Quotes Explained", item: `${SITE}/quotes` },
          ],
        }),
      },
    ],
  }),
  component: QuotesIndex,
});

function QuotesIndex() {
  const [q, setQ] = useState("");
  const [verdict, setVerdict] = useState<Verdict | "All">("All");

  const term = q.trim().toLowerCase();
  const filtered = QUOTE_CONTEXTS.filter((e) => {
    if (verdict !== "All" && e.verdict !== verdict) return false;
    if (!term) return true;
    return (
      e.quote.toLowerCase().includes(term) ||
      e.title.toLowerCase().includes(term) ||
      e.popularlyCreditedTo.toLowerCase().includes(term) ||
      e.actuallyFrom.toLowerCase().includes(term)
    );
  });

  return (
    <div className="min-h-screen">
      <SiteHeader sub="Quotes · Explained" active="quotes" />

      <main>
        <section className="border-b border-border/60">
          <div className="mx-auto max-w-6xl px-4 py-14 sm:py-20">
            <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
              <Link to="/" className="hover:text-gold">Home</Link>
              <span className="mx-2 opacity-50">/</span>
              <span className="text-foreground">Quotes Explained</span>
            </nav>
            <div className="mt-5 text-[10px] uppercase tracking-[0.3em] text-gold">Quote in context</div>
            <h1 className="mt-3 font-display text-4xl sm:text-6xl leading-tight max-w-3xl">
              Famous quotes explained — where they came from and what they actually meant
            </h1>
            <p className="mt-5 max-w-2xl text-lg text-muted-foreground leading-relaxed">
              Most famous lines have been shortened, re-credited, or quietly flipped on their way to
              you. Each page here traces one quote to its earliest solid record, explains the original
              point in plain English, shows how it gets misused, and gives you an honest way to use it.
              Where the record is genuinely uncertain, we say so instead of inventing a source.
            </p>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-4 py-10">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <label className="sr-only" htmlFor="quote-search">Search quotes</label>
            <input
              id="quote-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search a quote, e.g. carpe diem"
              className="w-full sm:max-w-sm rounded-full border border-border bg-card/50 px-5 py-2.5 text-sm outline-none focus:border-gold"
            />
            <div className="flex flex-wrap gap-2">
              {(["All", ...VERDICTS] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setVerdict(v as Verdict | "All")}
                  className={`rounded-full border px-3 py-1.5 text-xs transition ${
                    verdict === v
                      ? "border-gold/60 bg-gold/10 text-gold"
                      : "border-border text-muted-foreground hover:border-gold/40"
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((e) => (
              <Link
                key={e.slug}
                to="/quotes/$slug"
                params={{ slug: e.slug }}
                className="group flex flex-col rounded-2xl border border-border bg-card/40 p-6 transition hover:border-gold"
              >
                <div className="text-[10px] uppercase tracking-widest text-gold">{e.verdict}</div>
                <blockquote className="mt-3 font-display text-xl leading-snug group-hover:text-gold transition">
                  &ldquo;{e.quote}&rdquo;
                </blockquote>
                <p className="mt-3 text-sm text-muted-foreground line-clamp-3">{e.dek}</p>
                <div className="mt-4 border-t border-border/60 pt-3 text-xs text-muted-foreground">
                  Usually credited to <span className="text-foreground">{e.popularlyCreditedTo}</span>
                </div>
              </Link>
            ))}
          </div>

          {filtered.length === 0 && (
            <p className="mt-10 text-center text-muted-foreground">
              No quote matches that yet. Try another word.
            </p>
          )}
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
